import { FunctionComponent, MouseEvent, useState } from "react";

import { faCheck, faCopy } from "@fortawesome/pro-regular-svg-icons";
import { faBan } from "@fortawesome/pro-solid-svg-icons";

import { Icon } from "@/components";
import { copyText } from "@/utils/copyText";

interface BlacklistReasonPopoverProps {
	/** Heading of the hover: the blacklist type the row was given ("Competitor", …). */
	typeLabel: string;
	/** Why the row is blocked. Only a competitor verdict carries one. */
	reasoning?: string | null;
	/** Who decided, already translated ("AI-detected" / "Added by Ada Lovelace"). */
	attribution?: string | null;
}

const stop = (e: MouseEvent) => {
	e.preventDefault();
	e.stopPropagation();
};

/**
 * The reasoning hover behind a blacklist type (PREC-3594): the verdict, who made it, and the
 * reasoning text with click-to-copy. Deliberately the same card shell, divider and copy affordance
 * as the company/persona segment hovers, so the blacklist reads like the rest of the app.
 */
export const BlacklistReasonPopover: FunctionComponent<BlacklistReasonPopoverProps> = ({
	typeLabel,
	reasoning,
	attribution,
}) => {
	const [copied, setCopied] = useState(false);

	const copy = (e: MouseEvent) => {
		stop(e);

		if (!reasoning) {
			return;
		}

		void copyText(reasoning).then((copied) => {
			if (!copied) {
				return;
			}

			setCopied(true);
			setTimeout(() => setCopied(false), 1000);
		});
	};

	return (
		<div className="w-[300px]">
			<div className="flex items-center gap-1.5 px-2 pb-1 pt-0.5 text-[10px] font-bold uppercase tracking-wide text-text-meta">
				<Icon className="h-[12px] w-[12px]" icon={faBan} />
				{typeLabel}
			</div>

			{attribution && (
				<div className="px-2 pb-2 pt-1 text-[11.5px] text-text-meta">{attribution}</div>
			)}

			{reasoning && (
				<>
					<div className="my-1 h-px bg-gray-200" />
					<div
						className={`group/val max-h-[300px] cursor-pointer overflow-y-auto rounded-md px-2 py-1.5 transition-colors ${
							copied ? "bg-status-success-light" : "hover:bg-gray-50"
						}`}
						onClick={copy}
					>
						<div className="flex items-start gap-2">
							<span className="flex-1 whitespace-normal text-[11.5px] leading-relaxed text-text-meta">
								{reasoning}
							</span>
							<Icon
								className={`mt-0.5 h-[14px] w-[14px] shrink-0 transition-opacity ${
									copied
										? "text-status-success opacity-100"
										: "text-text-meta opacity-0 group-hover/val:opacity-100"
								}`}
								icon={copied ? faCheck : faCopy}
							/>
						</div>
					</div>
				</>
			)}
		</div>
	);
};
